<template>
  <div class="markdown-editor">
    <v-card outlined>
      <!-- Toolbar -->
      <v-toolbar dense flat color="grey lighten-4" v-if="!readonly">
        <v-btn-toggle v-model="activeTab" mandatory dense>
          <v-btn small value="edit">
            <v-icon left small>mdi-pencil</v-icon>
            {{ $t('edit') || 'Edit' }}
          </v-btn>
          <v-btn small value="preview">
            <v-icon left small>mdi-eye</v-icon>
            {{ $t('preview') || 'Preview' }}
          </v-btn>
        </v-btn-toggle>

        <v-divider vertical class="mx-2"></v-divider>

        <v-tooltip bottom v-if="activeTab === 'edit'">
          <template v-slot:activator="{ on }">
            <v-btn icon x-small v-on="on" @click="insertMarkdown('**', '**')">
              <v-icon small>mdi-format-bold</v-icon>
            </v-btn>
          </template>
          <span>Bold</span>
        </v-tooltip>

        <v-tooltip bottom v-if="activeTab === 'edit'">
          <template v-slot:activator="{ on }">
            <v-btn icon x-small v-on="on" @click="insertMarkdown('*', '*')">
              <v-icon small>mdi-format-italic</v-icon>
            </v-btn>
          </template>
          <span>Italic</span>
        </v-tooltip>

        <v-tooltip bottom v-if="activeTab === 'edit'">
          <template v-slot:activator="{ on }">
            <v-btn icon x-small v-on="on" @click="insertMarkdown('## ', '')">
              <v-icon small>mdi-format-header-2</v-icon>
            </v-btn>
          </template>
          <span>Heading</span>
        </v-tooltip>

        <v-tooltip bottom v-if="activeTab === 'edit'">
          <template v-slot:activator="{ on }">
            <v-btn icon x-small v-on="on" @click="insertMarkdown('- ', '')">
              <v-icon small>mdi-format-list-bulleted</v-icon>
            </v-btn>
          </template>
          <span>Bullet List</span>
        </v-tooltip>

        <v-tooltip bottom v-if="activeTab === 'edit'">
          <template v-slot:activator="{ on }">
            <v-btn icon x-small v-on="on" @click="insertMarkdown('1. ', '')">
              <v-icon small>mdi-format-list-numbered</v-icon>
            </v-btn>
          </template>
          <span>Numbered List</span>
        </v-tooltip>

        <v-tooltip bottom v-if="activeTab === 'edit'">
          <template v-slot:activator="{ on }">
            <v-btn icon x-small v-on="on" @click="insertMarkdown('[', '](url)')">
              <v-icon small>mdi-link</v-icon>
            </v-btn>
          </template>
          <span>Link</span>
        </v-tooltip>

        <v-tooltip bottom v-if="activeTab === 'edit'">
          <template v-slot:activator="{ on }">
            <v-btn icon x-small v-on="on" @click="insertMarkdown('`', '`')">
              <v-icon small>mdi-code-tags</v-icon>
            </v-btn>
          </template>
          <span>Code</span>
        </v-tooltip>

        <v-spacer></v-spacer>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon small v-on="on" @click="showHelp = !showHelp">
              <v-icon small>mdi-help-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>Markdown Help</span>
        </v-tooltip>
      </v-toolbar>

      <!-- Help Panel -->
      <v-expand-transition>
        <v-card-text v-if="showHelp" class="grey lighten-5 pa-3">
          <div class="text-caption">
            <strong>Markdown Quick Reference:</strong>
            <ul class="mt-2">
              <li><code>**bold**</code> → <strong>bold</strong></li>
              <li><code>*italic*</code> → <em>italic</em></li>
              <li><code>## Heading</code> → Heading</li>
              <li><code>- item</code> → Bullet list</li>
              <li><code>1. item</code> → Numbered list</li>
              <li><code>- [ ] task</code> → Interactive checkbox (unchecked)</li>
              <li><code>- [x] task</code> → Interactive checkbox (checked)</li>
              <li><code>[text](url)</code> → Link</li>
              <li><code>`code`</code> → Inline code</li>
            </ul>
          </div>
        </v-card-text>
      </v-expand-transition>

      <!-- Edit View -->
      <div v-show="activeTab === 'edit'">
        <v-textarea
          ref="textarea"
          :value="value"
          @input="onInput"
          :label="label"
          :placeholder="placeholder"
          :rows="rows"
          :auto-grow="autoGrow"
          outlined
          dense
          :hide-details="hideDetails"
          class="markdown-textarea"
        ></v-textarea>
      </div>

      <!-- Preview View -->
      <div v-show="activeTab === 'preview'" class="pa-4">
        <div v-if="value" class="markdown-content" @click="handleCheckboxClick">
          <VueMarkdown :source="renderedMarkdown" :breaks="true" :linkify="true" />
        </div>
        <div v-else class="grey--text text--lighten-1 text-caption">
          {{ placeholder || ($t('no_content') || 'No content to preview') }}
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import VueMarkdown from 'vue-markdown'

export default {
  name: 'MarkdownEditor',

  components: {
    VueMarkdown
  },

  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    rows: {
      type: [Number, String],
      default: 3
    },
    autoGrow: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      activeTab: 'preview',
      showHelp: false
    }
  },

  computed: {
    renderedMarkdown() {
      if (!this.value) return ''
      
      // Convert task list items to HTML checkboxes with data attributes
      let lineNumber = 0
      return this.value.split('\n').map(line => {
        const uncheckedMatch = line.match(/^(\s*[-*])\s+\[ \]\s+(.*)$/)
        const checkedMatch = line.match(/^(\s*[-*])\s+\[x\]\s+(.*)$/i)
        
        if (uncheckedMatch) {
          const result = `${uncheckedMatch[1]} <input type="checkbox" data-line="${lineNumber}" class="task-checkbox"> ${uncheckedMatch[2]}`
          lineNumber++
          return result
        } else if (checkedMatch) {
          const result = `${checkedMatch[1]} <input type="checkbox" checked data-line="${lineNumber}" class="task-checkbox"> ${checkedMatch[2]}`
          lineNumber++
          return result
        }
        return line
      }).join('\n')
    }
  },

  watch: {
    readonly: {
      immediate: true,
      handler(val) {
        if (val) {
          this.activeTab = 'preview'
        }
      }
    }
  },

  methods: {
    handleCheckboxClick(event) {
      const target = event.target
      if (target.tagName === 'INPUT' && target.type === 'checkbox' && target.classList.contains('task-checkbox')) {
        const lineNumber = parseInt(target.getAttribute('data-line'))
        this.toggleCheckbox(lineNumber)
      }
    },

    toggleCheckbox(lineIndex) {
      const lines = this.value.split('\n')
      let currentCheckboxIndex = 0
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const uncheckedMatch = line.match(/^(\s*[-*])\s+\[ \]\s+(.*)$/)
        const checkedMatch = line.match(/^(\s*[-*])\s+\[x\]\s+(.*)$/i)
        
        if (uncheckedMatch || checkedMatch) {
          if (currentCheckboxIndex === lineIndex) {
            if (uncheckedMatch) {
              // Change from unchecked to checked
              lines[i] = `${uncheckedMatch[1]} [x] ${uncheckedMatch[2]}`
            } else if (checkedMatch) {
              // Change from checked to unchecked
              lines[i] = `${checkedMatch[1]} [ ] ${checkedMatch[2]}`
            }
            break
          }
          currentCheckboxIndex++
        }
      }
      
      this.$emit('input', lines.join('\n'))
    },

    onInput(value) {
      this.$emit('input', value)
    },

    insertMarkdown(before, after) {
      const textarea = this.$refs.textarea.$refs.input
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selectedText = this.value.substring(start, end)
      const beforeText = this.value.substring(0, start)
      const afterText = this.value.substring(end)

      const newText = beforeText + before + selectedText + after + afterText
      this.$emit('input', newText)

      // Set cursor position after insertion
      this.$nextTick(() => {
        const newCursorPos = start + before.length + selectedText.length
        textarea.focus()
        textarea.setSelectionRange(newCursorPos, newCursorPos)
      })
    }
  }
}
</script>

<style scoped>
.markdown-editor {
  width: 100%;
}

.markdown-textarea {
  font-family: 'Courier New', monospace;
}

.markdown-content {
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.87);
}

.markdown-content >>> h1,
.markdown-content >>> h2,
.markdown-content >>> h3,
.markdown-content >>> h4,
.markdown-content >>> h5,
.markdown-content >>> h6 {
  margin-top: 16px;
  margin-bottom: 8px;
  font-weight: 600;
}

.markdown-content >>> h1 { font-size: 2em; }
.markdown-content >>> h2 { font-size: 1.5em; }
.markdown-content >>> h3 { font-size: 1.25em; }
.markdown-content >>> h4 { font-size: 1em; }

.markdown-content >>> p {
  margin-bottom: 12px;
}

.markdown-content >>> ul,
.markdown-content >>> ol {
  margin-bottom: 12px;
  padding-left: 24px;
}

.markdown-content >>> li {
  margin-bottom: 4px;
}

.markdown-content >>> code {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}

.markdown-content >>> pre {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  margin-bottom: 12px;
}

.markdown-content >>> pre code {
  background-color: transparent;
  padding: 0;
}

.markdown-content >>> blockquote {
  border-left: 4px solid #e0e0e0;
  padding-left: 16px;
  margin: 12px 0;
  color: #757575;
}

.markdown-content >>> a {
  color: #1976d2;
  text-decoration: none;
}

.markdown-content >>> a:hover {
  text-decoration: underline;
}

.markdown-content >>> strong {
  font-weight: 600;
}

.markdown-content >>> em {
  font-style: italic;
}

.markdown-content >>> hr {
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 16px 0;
}

.markdown-content >>> table {
  border-collapse: collapse;
  width: 100%;
  margin-bottom: 12px;
}

.markdown-content >>> table th,
.markdown-content >>> table td {
  border: 1px solid #e0e0e0;
  padding: 8px;
  text-align: left;
}

.markdown-content >>> table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

/* Task list checkboxes */
.markdown-content >>> .task-checkbox {
  margin-right: 8px;
  cursor: pointer;
  width: 16px;
  height: 16px;
  vertical-align: middle;
}

.markdown-content >>> .task-checkbox:hover {
  opacity: 0.8;
}

/* Remove default list styling for task lists */
.markdown-content >>> ul li:has(.task-checkbox) {
  list-style: none;
  margin-left: -24px;
}
</style>
